"use client"

import Intercom from "@intercom/messenger-js-sdk"
import { useEffect } from "react"
import { INTERCOM_API_BASE, INTERCOM_APP_ID } from "@/lib/intercom/config"

type AnonymousProps = {
  mode: "anonymous"
}

type IdentifiedProps = {
  mode: "identified"
  userId: string
  email: string | null
  userJwt: string
  /** App-name segment carried into the workspace (e.g. "Siro", "Field Pro"). */
  tenant: string
  name?: string
  phoneNumber?: string | null
  /** ISO timestamp; converted to Intercom's unix-seconds `created_at`. */
  createdAt?: string | null
  organizationId?: string
  organizationName?: string | null
  organizationBetaFeatures?: readonly string[]
  hasSiroCoachEnabled?: boolean
  memberTeamId?: string | null
  coachTeamIds?: readonly string[]
  betaFeatures?: readonly string[]
  /** Org admin or above, per `can(user, Organization, ORG, Edit)`. */
  isAdmin: boolean
  industryIds?: readonly string[]
}

type Props = AnonymousProps | IdentifiedProps

// Intercom custom attributes are capped at 255 chars and have no array type,
// so list-shaped data is joined into a comma-separated string
const MAX_ATTRIBUTE_LENGTH = 255
const joinForIntercom = (
  values: readonly string[] | undefined,
): string | undefined =>
  values?.length ? values.join(", ").slice(0, MAX_ATTRIBUTE_LENGTH) : undefined

const toUnixSeconds = (iso: string | null | undefined): number | undefined =>
  iso ? Math.floor(new Date(iso).getTime() / 1000) : undefined

// Boots the Intercom Messenger. The SDK is idempotent — calling
// `Intercom({...})` again with new args updates the running session, so this
// component is safe to re-render and safe to mount alongside a different
// variant elsewhere in the tree.
//
// Mounted in two places intentionally:
//   - `/login` (anonymous) → launcher visible so signed-out visitors can chat.
//   - org-scoped layout (identified) → launcher hidden; users open the
//     messenger via the Help button in the sidebar.
export const IntercomBootstrap = (props: Props) => {
  useEffect(() => {
    if (props.mode === "anonymous") {
      Intercom({
        app_id: INTERCOM_APP_ID,
        api_base: INTERCOM_API_BASE,
        hide_default_launcher: false,
      })
      return
    }
    const payload = {
      app_id: INTERCOM_APP_ID,
      api_base: INTERCOM_API_BASE,
      user_id: props.userId,
      email: props.email ?? undefined,
      intercom_user_jwt: props.userJwt,
      tenant: props.tenant,
      hide_default_launcher: true,
      name: props.name,
      phone: props.phoneNumber ?? undefined,
      created_at: toUnixSeconds(props.createdAt),
      ...(props.organizationId
        ? {
            company: {
              id: props.organizationId,
              name: props.organizationName ?? undefined,
              betaFeatures: joinForIntercom(props.organizationBetaFeatures),
            },
          }
        : {}),
      orgName: props.organizationName ?? undefined,
      hasSiroCoachEnabled: props.hasSiroCoachEnabled ? "true" : "false",
      memberTeamId: props.memberTeamId ?? undefined,
      coachTeamIds: joinForIntercom(props.coachTeamIds),
      betaFeatures: joinForIntercom(props.betaFeatures),
      isAdmin: props.isAdmin,
      industryIds: joinForIntercom(props.industryIds),
    }
    Intercom(payload)
  }, [props])

  return null
}
